import { useState } from 'react'
import {
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
} from '@mui/material'
import AssignmentIcon from '@mui/icons-material/Assignment'
import PersonIcon from '@mui/icons-material/Person'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import WarningIcon from '@mui/icons-material/Warning'
import PrintIcon from '@mui/icons-material/Print'
import SummaryCard from './SummaryCard'
import RosterForm from 'src/supportPortal/views/roster/component/RosterForm'
import { Dialog } from '@mui/material'

export default function SummaryView({
  summary,
  unoccupiedSlots,
  fromDate,
  toDate,
  loading,
}) {
  const [selectedUnoccupiedSlot, setSelectedUnoccupiedSlot] = useState(null)
  const [showAssignDialog, setShowAssignDialog] = useState(false)

  if (loading || !summary) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading summary...</Typography>
      </Box>
    )
  }

  const coveragePercentage = summary.totalSlots > 0
    ? ((summary.occupiedSlots / summary.totalSlots) * 100).toFixed(1)
    : 0

  const handleAssignClick = (slot) => {
    setSelectedUnoccupiedSlot(slot)
    setShowAssignDialog(true)
  }

  const handleCloseDialog = () => {
    setShowAssignDialog(false)
    setSelectedUnoccupiedSlot(null)
  }

  const handlePrint = () => {
    // Add style tag for portrait orientation
    const style = document.createElement('style')
    style.id = 'print-orientation-style'
    style.textContent = '@page { size: portrait; margin: 0.3cm; }'
    document.head.appendChild(style)
    
    // Trigger print
    window.print()
    
    // Remove style after printing
    setTimeout(() => {
      const printStyle = document.getElementById('print-orientation-style')
      if (printStyle) {
        printStyle.remove()
      }
    }, 1000)
  }

  return (
    <Box 
      sx={{ 
        p: 3, 
        height: '100%', 
        overflow: 'auto',
        backgroundColor: (theme) => theme.palette.mode === 'dark'
          ? 'rgba(18, 18, 18, 0.95)'
          : 'rgba(250, 250, 252, 0.98)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
      }} 
      className="roster-summary-view"
    >
      {/* Print Button */}
      <Box
        className="no-print"
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mb: 2,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Print Summary
        </Button>
      </Box>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }} className="no-print">
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Total Slots"
            value={summary.totalSlots || 0}
            subtitle="All roster slots"
            icon={AssignmentIcon}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Occupied"
            value={summary.occupiedSlots || 0}
            subtitle={`${coveragePercentage}% coverage`}
            icon={EventAvailableIcon}
            color="success"
            trend={1}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Unoccupied"
            value={summary.unoccupiedSlots || 0}
            subtitle="Available for assignment"
            icon={EventBusyIcon}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Coverage"
            value={`${coveragePercentage}%`}
            subtitle="Occupied / Total"
            icon={TrendingUpIcon}
            color="info"
          />
        </Grid>
      </Grid>

      {/* Additional Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }} className="no-print">
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PersonIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Practitioners
              </Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
              {summary.uniquePractitioners || 0}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Active practitioners with assignments
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <WarningIcon color="error" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Conflicts
              </Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>
              {summary.conflicts || 0}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Overlapping assignments detected
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Date Range
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {fromDate.format('MMM D')} - {toDate.format('MMM D, YYYY')}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* By Clinic Breakdown */}
      {summary.byClinic && summary.byClinic.length > 0 && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }} className="roster-table-container">
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            By Clinic
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Clinic</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Total Slots
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Occupied
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Unoccupied
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Coverage
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.byClinic.map((clinic) => {
                  const clinicCoverage =
                    clinic.totalSlots > 0
                      ? ((clinic.occupiedSlots / clinic.totalSlots) * 100).toFixed(1)
                      : 0
                  return (
                    <TableRow key={clinic.clinicBrandId || clinic.clinicId} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {clinic.clinicBrandName || clinic.clinicName}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{clinic.totalSlots || 0}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={clinic.occupiedSlots || 0}
                          color="success"
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={clinic.unoccupiedSlots || 0}
                          color="warning"
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: clinicCoverage >= 70 ? 'success.main' : 'warning.main',
                          }}
                        >
                          {clinicCoverage}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* By Practitioner Breakdown */}
      {summary.byPractitioner && summary.byPractitioner.length > 0 && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }} className="roster-table-container">
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            By Practitioner
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Practitioner</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Total Hours
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Clinics
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Coverage
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.byPractitioner.slice(0, 10).map((practitioner) => (
                  <TableRow key={practitioner.mentorId || practitioner.practitionerId} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {practitioner.mentorName || practitioner.practitionerName}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{practitioner.totalHours || 0} hrs</TableCell>
                    <TableCell align="right">{practitioner.clinicCount || 0}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${practitioner.coveragePercentage || 0}%`}
                        color={practitioner.coveragePercentage >= 80 ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* By Day Breakdown */}
      {summary.byDay && summary.byDay.length > 0 && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }} className="roster-table-container">
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            By Day
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Total Slots
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Occupied
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Unoccupied
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Coverage
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.byDay.map((day) => {
                  const dayCoverage =
                    day.totalSlots > 0
                      ? ((day.occupiedSlots / day.totalSlots) * 100).toFixed(1)
                      : 0
                  return (
                    <TableRow key={day.date} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {day.date}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{day.totalSlots || 0}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={day.occupiedSlots || 0}
                          color="success"
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={day.unoccupiedSlots || 0}
                          color="warning"
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: dayCoverage >= 70 ? 'success.main' : 'warning.main',
                          }}
                        >
                          {dayCoverage}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Unoccupied Slots */}
      {unoccupiedSlots && unoccupiedSlots.length > 0 && (
        <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 2 }} className="roster-table-container">
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Unoccupied Slots (Top 20)
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Room</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Clinic</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unoccupiedSlots.slice(0, 20).map((slot, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{slot.customDate}</TableCell>
                    <TableCell>
                      {slot.slotStartTime} - {slot.slotEndTime}
                    </TableCell>
                    <TableCell>{slot.chamberRoomTitle || `Room ${slot.chamberRoomId}`}</TableCell>
                    <TableCell>
                      {slot.clinicBrandName}
                      {slot.clinicLocationName && ` - ${slot.clinicLocationName}`}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={(event) => handleAssignClick(slot, event)}
                      >
                        Assign
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        {selectedUnoccupiedSlot && (
          <RosterForm
            selectedSlot={selectedUnoccupiedSlot}
            closePopup={handleCloseDialog}
            // Add other required props
          />
        )}
      </Dialog>
    </Box>
  )
}
